import { UIIcon } from '../config/icons';
import { EDNewBlock } from '../lib/ed/block';
import { EDEditor } from '../lib/ed/editor';
import { EDFields } from '../lib/ed/fields';
import { ed_page_bubble_block, ed_page_hline_block, ed_page_table_block } from '../lib/ed/page/basic';
import { ed_page_buttons_block } from '../lib/ed/page/button';
import { ed_page_cards_block } from '../lib/ed/page/cards';
import { ed_page_container_block } from '../lib/ed/page/container';
import { ed_page_carousels_block, ed_page_cityselect_block, ed_page_events_block, ed_page_featured_block } from '../lib/ed/page/events';
import { ed_page_hero_block, ed_page_subscribe_button_block } from '../lib/ed/page/hero';
import { ed_page_banner_block, ed_page_images_block } from '../lib/ed/page/image';
import { ed_page_info_block } from '../lib/ed/page/info';
import { ed_page_faq_block, ed_page_map_block, ed_page_testimonial_block } from '../lib/ed/page/landing';
import { ed_page_hsplit_block } from '../lib/ed/page/layout';
import { ed_page_app_rating_block, ed_page_hubspot_block, ed_page_newsletter_block } from '../lib/ed/page/marketing';
import { ed_page_plans_block } from '../lib/ed/page/plans';
import { EDPageSection } from '../lib/ed/page/section';
import { ed_page_text_block } from '../lib/ed/page/text';
import { ed_page_video_block } from '../lib/ed/page/video';
import { FPageSectionView, FSection } from '../lib/mdl/f-page';
import { FPageBlock } from '../lib/mdl/f-page-block';
import { UIValueGroup } from '../lib/ui/component/groupdropdown';
import { UIClassesSpec } from '../lib/ui/types';

export class AdminPageEditor extends EDEditor<FSection, FPageBlock> {
    constructor(page: FPageSectionView, classes?: UIClassesSpec, translate_mode?: boolean) {
        // keep this, because it's where we can modify blocks to newer versions
        // if(xavis_experiments) {
        //     for(const s of page.sections) {
        //         for(let i = 0; i < s.blocks.length; i++) {
        //             const b = s.blocks[i];
        //         }
        //     }
        // }

        const fields = new EDFields([
            { key: 'city.events', name: 'Active events' },
            { key: 'user.city', name: 'User city' },
            { key: 'promo.button', name: 'Promo button' },
            { key: 'promo.subtitle', name: 'Promo subtitle' }
        ]);

        const new_blocks: UIValueGroup<EDNewBlock<FPageBlock>>[] = [
            {
                name: 'Content', values: [
                    { name: 'Text', icon: UIIcon.TEXT, block: { type: 'text', text: {} } },
                    { name: 'Images', icon: UIIcon.IMAGES, block: { type: 'images', layout: 'flow', images: [] } },
                    { name: 'Horizontal Line', icon: UIIcon.HORIZONTAL_RULE, block: { type: 'hline' } },
                    { name: 'Buttons', icon: UIIcon.RECTANGLE_WIDE, block: { type: 'buttons', buttons: [{ color: 'tint', text: {}, link: '', kind: 'k-default', hollow: false, larger: false }], column: false, align: 'left' } },
                    { name: 'Video', experimental: true, icon: UIIcon.VIDEO, block: { type: 'video' } },
                ]
            },
            {
                name: 'Layout', values: [
                    { name: 'Bubble', icon: UIIcon.MESSAGE, block: { type: 'bubble', kind: 'default', blocks: [], align: 'start' } },
                    { name: 'Horizontal Split', icon: UIIcon.PAUSE, block: { type: 'hsplit', blocks: [{ type: 'container', kind: 'full', blocks: [], bg: {} }, { type: 'container', kind: 'full', blocks: [], bg: {} }], reverse: false, wrap: 'normal', align: 'center' } },
                    { name: 'Table', icon: UIIcon.TABLE, block: { type: 'table', header: [{}], rows: [[{}]] } },
                    { name: 'Cards', icon: UIIcon.CARDS_BLANK, block: { type: 'cards', items: [{ title: {}, text: {} }] } },
                    { name: 'Info', icon: UIIcon.IMAGE_STACK, block: { type: 'info', kind: 'default', items: [{ text: {} }] } },
                    { name: 'FAQ', icon: UIIcon.QUESTION, block: { type: 'faq', groups: [{ title: {}, items: [{ q: {}, a: {} }] }], headers: true, expanded: false } },
                    { name: 'Testimonials', icon: UIIcon.MESSAGES, block: { type: 'testimonial', pink: true, items: [{ header: {}, text: {} }] } },
                ]
            },
            {
                name: 'Landing', values: [
                    { name: 'Hero', icon: UIIcon.HOUSE, block: { type: 'new_hero', blocks: [] } },
                    { name: 'Plans', icon: UIIcon.TAG, block: { type: 'plans' } },
                    { name: 'Subscribe button', icon: UIIcon.ROCKET_LAUNCH, block: { type: 'subscribe_button' } },
                    { name: 'Banner', icon: UIIcon.ELLIPSIS_STROKE, block: { type: 'banner', images: [], symbol: 'circle-eye-3', text: {} } },
                ]
            },
            {
                name: 'Events', values: [
                    { name: 'Carousels', icon: UIIcon.CALENDAR, block: { type: 'carousels' } },
                    { name: 'Events', icon: UIIcon.CALENDAR_DAYS, block: { type: 'events', query: 'city', sort: 'date', randomize: false, display: 'row', shape: 'event', show_button: true, show_header: true, color_scheme: 'inherit', header: {} } },
                    { name: 'Featured', icon: UIIcon.CALENDAR_STAR, block: { type: 'featured' } },
                    { name: 'City selection', icon: UIIcon.BUILDINGS, block: { type: 'cityselect' } },
                    { name: 'Map', icon: UIIcon.MAP_LOCATION, block: { type: 'map' } },
                ]
            },
            {
                name: 'Marketing', values: [
                    { name: 'Hubspot', icon: UIIcon.BRAND_HUBSPOT, block: { type: 'hubspot', portal_id: '', form_id: '', region: '' } },
                    { name: 'Newsletter', icon: UIIcon.ENVELOPE, block: { type: 'newsletter' } },
                    { name: 'App ratings', icon: UIIcon.STAR, block: { type: 'app_rating', split: false } },
                ]
            },
        ];

        const block_specs = [
            ed_page_app_rating_block,
            ed_page_banner_block,
            ed_page_bubble_block,
            ed_page_buttons_block,
            ed_page_cards_block,
            ed_page_carousels_block,
            ed_page_cityselect_block,
            ed_page_container_block,
            ed_page_events_block,
            ed_page_faq_block,
            ed_page_featured_block,
            ed_page_hline_block,
            ed_page_hsplit_block,
            ed_page_hubspot_block,
            ed_page_images_block,
            ed_page_info_block,
            ed_page_map_block,
            ed_page_hero_block,
            ed_page_newsletter_block,
            ed_page_plans_block,
            ed_page_subscribe_button_block,
            ed_page_table_block,
            ed_page_testimonial_block,
            ed_page_text_block,
            ed_page_video_block,
        ]

        super(fields, block_specs, new_blocks, page.name, Object.keys(page.languages), classes, translate_mode);
    }

    protected override makeSection() {
        return new EDPageSection(this);
    }

    protected override newSection(): FSection {
        return {
            countries: {},
            properties: { bg: {}, color_scheme: 'inherit', padding_top: 'narrow', padding_bottom: 'narrow', header: false, bg_sticky: false },
            blocks: []
        } as unknown as FSection;
    }
}
